import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { LayoutGrid, PlayCircle, Calendar, CheckCircle2, Clock, Radio } from 'lucide-react';
import { sessionService } from '../../services';
import exportService from '../../services/exportService';
import Pagination from '../../components/ui/Pagination';
import ResponsiveDataTable from '../../components/ui/ResponsiveDataTable';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Admin Sessions Page
 * View all sessions across all organizations (Super Admin only)
 */

const AdminSessionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgFilter = searchParams.get('org');
  const orgNameParam = searchParams.get('orgName');
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, live, scheduled, completed
  const [timeRange, setTimeRange] = useState('all'); // all, 7d, 14d, 30d
  const [searchTerm, setSearchTerm] = useState('');
  const [orgName, setOrgName] = useState(orgNameParam || '');
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [stats, setStats] = useState({
    total: 0,
    live: 0,
    scheduled: 0,
    completed: 0,
    expired: 0,
  });
  const [platformInsights, setPlatformInsights] = useState({
    avgParticipation: 0,
    participationChange: 0,
    avgDuration: 0,
    totalParticipants: 0,
  });

  // Fetch stats once on mount (unfiltered) — stats should not change with filters
  const fetchStats = async () => {
    try {
      const params = { limit: 500 };
      if (orgFilter) params.organization = orgFilter;
      const response = await sessionService.getSessions(params);
      const allSessions = (response.data || response || []).map(session => {
        const baseSession = {
          ...session,
          status: session.status,
          startTime: session.startTime || session.scheduledStart,
          endTime: session.endTime || session.scheduledEnd,
        };
        baseSession.status = getEffectiveStatus(baseSession);
        return baseSession;
      });
      setStats({
        total: allSessions.length,
        live: allSessions.filter(s => s.status === 'live').length,
        scheduled: allSessions.filter(s => s.status === 'scheduled').length,
        completed: allSessions.filter(s => s.status === 'completed').length,
        expired: allSessions.filter(s => s.status === 'expired').length,
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [orgFilter]);

  useEffect(() => {
    fetchSessions();
  }, [filter, orgFilter]);

  // Helper function to calculate effective status based on time.
  // A live session stays live until the trainer ends it — sessions routinely
  // overrun their planned end time and must not be shown as expired.
  // Only a session that was scheduled and never started expires once its window passes.
  // (A scheduled session whose start time has passed stays scheduled — starting is manual.)
  const getEffectiveStatus = (session) => {
    if (session.status !== 'scheduled') {
      return session.status;
    }

    const now = new Date();
    const endTime = session.endTime ? new Date(session.endTime) : null;

    if (endTime && !isNaN(endTime.getTime()) && endTime < now) {
      return 'expired';
    }

    return session.status;
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filter !== 'all') params.status = filter;
      if (searchTerm) params.search = searchTerm;
      if (orgFilter) params.organization = orgFilter;

      const response = await sessionService.getSessions(params);

      // Transform API data to match frontend format
      const transformedSessions = response.data.map(session => {
        const baseSession = {
          id: session._id,
          name: session.name || session.title,
          code: session.code || session.sessionCode,
          status: session.status,
          trainerName: session.trainer?.firstName && session.trainer?.lastName
            ? `${session.trainer.firstName} ${session.trainer.lastName}`
            : session.trainerName || 'Unknown',
          organizationName: session.organization?.name || 'Unknown',
          organizationId: session.organization?._id,
          startTime: session.startTime || session.scheduledStart,
          endTime: session.endTime || session.scheduledEnd,
          currentParticipants: session.currentParticipants || session.participantCount || session.participants?.filter(p => p.status === 'joined').length || session.participants?.length || 0,
          expectedParticipants: session.expectedParticipants,
          maxParticipants: session.maxParticipants || 30,
        };
        // Apply effective status based on time
        baseSession.status = getEffectiveStatus(baseSession);
        return baseSession;
      });

      setSessions(transformedSessions);

      // Extract org name when filtering by organization
      if (orgFilter && transformedSessions.length > 0) {
        setOrgName(transformedSessions[0].organizationName || '');
      }

      // Stats are fetched separately (unfiltered) — don't recalculate here

      // Calculate platform insights from real data
      const sessionsWithParticipants = transformedSessions.filter(s => s.maxParticipants > 0);
      const totalParticipants = transformedSessions.reduce((sum, s) => sum + (s.currentParticipants || 0), 0);

      // Calculate average participation rate (currentParticipants / maxParticipants)
      let avgParticipation = 0;
      if (sessionsWithParticipants.length > 0) {
        const participationRates = sessionsWithParticipants.map(s =>
          (s.currentParticipants / (s.expectedParticipants || s.maxParticipants)) * 100
        );
        avgParticipation = Math.round(participationRates.reduce((a, b) => a + b, 0) / participationRates.length);
      }

      // Calculate average duration in hours
      let avgDuration = 0;
      const validDurations = [];

      transformedSessions.forEach(s => {
        // First try to use duration field if available (in minutes)
        if (s.duration && s.duration > 0) {
          validDurations.push(s.duration / 60); // Convert minutes to hours
        } else if (s.startTime && s.endTime) {
          // Calculate from start/end times
          const start = new Date(s.startTime);
          const end = new Date(s.endTime);
          // Only use if both dates are valid and duration is positive
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            const durationHours = (end - start) / (1000 * 60 * 60);
            if (durationHours > 0) {
              validDurations.push(durationHours);
            }
          }
        }
      });

      if (validDurations.length > 0) {
        avgDuration = validDurations.reduce((a, b) => a + b, 0) / validDurations.length;
      }

      setPlatformInsights({
        avgParticipation,
        participationChange: 0, // Would need historical data to calculate
        avgDuration: Math.round(avgDuration * 10) / 10, // Round to 1 decimal
        totalParticipants,
      });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  // Get days for time range
  const getTimeRangeDays = (range) => {
    switch (range) {
      case '7d': return 7;
      case '14d': return 14;
      case '30d': return 30;
      default: return null; // all
    }
  };

  // Filter sessions locally for instant feedback
  const filteredSessions = sessions.filter((session) => {
    const matchesSearch =
      searchTerm === '' ||
      session.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.trainerName.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by status (including expired which is computed on frontend)
    const matchesFilter = filter === 'all' || session.status === filter;

    // Filter by time range
    const days = getTimeRangeDays(timeRange);
    let matchesTimeRange = true;
    if (days && session.startTime) {
      const sessionDate = new Date(session.startTime);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      matchesTimeRange = sessionDate >= cutoffDate;
    }

    return matchesSearch && matchesFilter && matchesTimeRange;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const paginatedSessions = filteredSessions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filter, timeRange]);

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      await exportService.exportSessionsCSV(params);
      toast.success('Sessions exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export sessions');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSelectSession = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(selectedSessions.filter((id) => id !== sessionId));
    } else {
      setSelectedSessions([...selectedSessions, sessionId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === paginatedSessions.length && paginatedSessions.length > 0) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(paginatedSessions.map((s) => s.id));
    }
  };

  return (
    <DashboardLayout title={orgFilter && orgName ? `${orgName} - Sessions` : 'All Sessions'}>
      <div className="p-4 space-y-4 pb-20 sm:pb-6">
        {/* Org filter banner */}
        {orgFilter && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
            <span className="text-sm text-amber-800">
              Showing sessions for: <strong>{orgName || 'Organization'}</strong>
            </span>
            <button
              onClick={() => navigate('/admin/sessions')}
              className="text-sm font-medium text-amber-700 hover:text-amber-900 underline"
            >
              Show All Sessions
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-3">
          <button
            onClick={() => orgFilter ? navigate(`/admin/organizations/${orgFilter}`) : navigate('/admin/dashboard')}
            className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-neutral-900 transition-colors mb-3 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">{orgFilter ? 'Back to Organization' : 'Back to Dashboard'}</span>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-neutral-900">{orgFilter && orgName ? `${orgName} Sessions` : 'Platform Sessions'}</h1>
            <p className="text-xs text-neutral-600">All sessions across all organizations</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              <span className="hidden sm:inline ml-1.5">{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <StatCard label="Total"     value={stats.total}     icon={LayoutGrid}   variant="slate"   onClick={() => setFilter('all')}       active={filter === 'all'} />
          <StatCard label="Live"      value={stats.live}      icon={PlayCircle}   variant="emerald" onClick={() => setFilter('live')}      active={filter === 'live'} />
          <StatCard label="Scheduled" value={stats.scheduled} icon={Calendar}     variant="blue"    onClick={() => setFilter('scheduled')} active={filter === 'scheduled'} />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} variant="violet"  onClick={() => setFilter('completed')} active={filter === 'completed'} />
          <StatCard label="Expired"   value={stats.expired}   icon={Clock}        variant="rose"    onClick={() => setFilter('expired')}   active={filter === 'expired'} />
        </div>

        {/* Bulk Actions Bar */}
        {selectedSessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-2.5"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-blue-900">
                {selectedSessions.length} session{selectedSessions.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setSelectedSessions([])}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sessions Table Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by session name, code, or trainer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-neutral-50/50 placeholder:text-neutral-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Status</option>
                  <option value="live">Live</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </select>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All time</option>
                  <option value="7d">Last 7 days</option>
                  <option value="14d">Last 14 days</option>
                  <option value="30d">Last 30 days</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table / Cards */}
          <ResponsiveDataTable
            columns={[
              {
                key: 'select',
                label: '',
                card: false,
                stopPropagation: true,
                headerClassName: 'w-10',
                render: (s) => (
                  <input
                    type="checkbox"
                    checked={selectedSessions.includes(s.id)}
                    onChange={() => handleSelectSession(s.id)}
                    className="w-3.5 h-3.5 text-primary-600 rounded"
                  />
                ),
              },
              {
                key: 'session',
                label: 'Session',
                card: 'header',
                render: (s) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">{s.name}</p>
                      <p className="text-xs text-neutral-400">Code: {s.code}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: 'organization',
                label: 'Organization',
                card: 'body',
                render: (s) => <span className="text-sm text-neutral-700">{s.organizationName}</span>,
              },
              {
                key: 'trainer',
                label: 'Trainer',
                card: 'body',
                render: (s) => <span className="text-sm text-neutral-700">{s.trainerName}</span>,
              },
              {
                key: 'datetime',
                label: 'Date & Time',
                card: 'body',
                render: (s) => (
                  <div>
                    <p className="text-sm text-neutral-900">{s.startTime ? formatDateWithTimezone(s.startTime) : 'Not set'}</p>
                    <p className="text-xs text-neutral-400">{formatTime(s.startTime)} - {formatTime(s.endTime)}</p>
                  </div>
                ),
              },
              {
                key: 'participants',
                label: 'Participants',
                card: 'body',
                render: (s) => (
                  <span className="text-sm text-neutral-700">
                    {s.currentParticipants || 0}/{s.expectedParticipants || s.maxParticipants}
                  </span>
                ),
              },
              {
                key: 'status',
                label: 'Status',
                card: 'body',
                render: (s) => <Badge variant={s.status} size="sm" dot={s.status === 'live'}>{s.status}</Badge>,
              },
              {
                key: 'actions',
                label: '',
                card: 'footer',
                align: 'right',
                stopPropagation: true,
                render: (s) => {
                  // Monitor is most useful while a session is in flight, so the
                  // button is visually emphasized for live/paused sessions and
                  // muted for everything else (where it shows the final
                  // snapshot + event feed for post-incident review).
                  const isLive = s.status === 'live' || s.status === 'paused';
                  return (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => navigate(`/admin/sessions/${s.id}/monitor`)}
                        className={`p-2 rounded-lg transition-colors ${
                          isLive
                            ? 'text-rose-600 hover:text-rose-700 hover:bg-rose-50 ring-1 ring-inset ring-rose-200/70'
                            : 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100'
                        }`}
                        title={isLive ? 'Live monitor' : 'Open monitor'}
                      >
                        <Radio className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/sessions/${s.id}/report`)}
                        className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                        title="View Report"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </div>
                  );
                },
              },
            ]}
            data={paginatedSessions}
            keyExtractor={(s) => s.id}
            onRowClick={(s) => navigate(`/admin/sessions/${s.id}/report`)}
            tableMinWidth="min-w-[900px]"
            emptyState={
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600 font-medium mb-1">No sessions found</p>
                <p className="text-xs text-neutral-400">
                  {searchTerm ? 'Try adjusting your search' : `No ${filter === 'all' ? '' : filter} sessions available`}
                </p>
              </div>
            }
          />

          {/* Pagination */}
          {filteredSessions.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredSessions.length}
              />
            </div>
          )}
        </div>

        {/* Platform Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-neutral-100 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2.5 rounded-xl">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500">Avg. Participation</p>
                <p className="text-base sm:text-lg font-bold text-neutral-900">{platformInsights.avgParticipation}%</p>
                <p className="text-xs text-neutral-400">Across all sessions</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-100 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2.5 rounded-xl">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500">Avg. Duration</p>
                <p className="text-base sm:text-lg font-bold text-neutral-900">{platformInsights.avgDuration} hrs</p>
                <p className="text-xs text-neutral-400">Across all sessions</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-neutral-100 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2.5 rounded-xl">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-medium text-neutral-500">Total Participants</p>
                <p className="text-base sm:text-lg font-bold text-neutral-900">{platformInsights.totalParticipants.toLocaleString()}</p>
                <p className="text-xs text-neutral-400">Across all sessions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminSessionsPage;
